import { useEffect, useRef } from 'react';

/**
 * Letter -> action. A falsy action leaves that chord unbound, so the keypress
 * falls through untouched — that's how a disabled affordance (Save with nothing
 * to save, Close in page mode) stays inert without the caller branching.
 */
export type AltHotkeyBindings = Record<string, (() => void) | null | undefined | false>;

export type AltHotkeyOptions = {
  /**
   * Set by a modal/overlay to claim the top hotkey layer for as long as it's
   * mounted. While any layer is claimed, chords reach ONLY the topmost one —
   * every surface beneath it goes inert, including for letters the top layer
   * hasn't bound. That's deliberate: Alt+S on an open modal must never fall
   * through and save the record behind it.
   *
   * Overlays that stay mounted while closed pass their open state here
   * (`{ layer: isOpen }`). An explicit `false` means "overlay, currently
   * closed" and binds NOTHING — distinct from omitting `options` entirely,
   * which marks a base surface like the Pro drawer footer. Without that
   * distinction a closed-but-mounted modal would answer chords meant for the
   * surface behind it.
   */
  layer?: boolean;
};

/**
 * Claimed layers, oldest first — the last entry owns the keyboard. Module-level
 * because layers cross React trees: modals portal to <body>, so no provider
 * reliably wraps both the overlay and the surface it covers.
 */
const layerStack: number[] = [];
let nextLayerId = 1;

/**
 * Binds Alt+letter chords — the same shortcuts V1 gets for free from `accesskey`
 * (Alt+letter on Windows/Chrome, Option+letter on macOS, Alt+Shift+letter in
 * Firefox), which is where our users' muscle memory comes from.
 *
 * - Matches `event.code`, not `event.key`: macOS turns Option+V into '√'.
 * - Requires Alt and rejects Ctrl/Cmd, so browser shortcuts survive and a chord
 *   can never fire while the user is typing into a form. No input exemptions
 *   needed as a result.
 * - `options.layer` is how an overlay takes ownership of the keyboard: while a
 *   layer is claimed, only the topmost claimant answers any chord. This is the
 *   load-bearing guard — it needs no `role="dialog"` and no focus inside the
 *   overlay, so it holds for portalled modals and modals that focus nothing.
 * - `scopeRef` should point at any element inside the surface that owns the
 *   chord. It backs a secondary DOM guard for overlays that have NOT claimed a
 *   layer: a keypress from inside a `role="dialog"`/`aria-modal` element that
 *   doesn't contain the scope is skipped. Dialogs that WRAP the scope (the Pro
 *   drawer itself) still fire. That guard only bites when focus is actually
 *   inside the dialog, which is why `layer` exists — prefer it for new overlays.
 *
 * Bindings are read through a ref, so passing a fresh object every render costs
 * nothing — the window listener is installed once.
 */
export function useAltHotkeys(
  bindings: AltHotkeyBindings,
  scopeRef?: React.RefObject<HTMLElement | null>,
  options?: AltHotkeyOptions,
): void {
  const latest = useRef(bindings);
  useEffect(() => {
    latest.current = bindings;
  });

  const isLayer = options?.layer === true;
  const inert = options?.layer === false;
  const layerId = useRef(0);
  if (isLayer && layerId.current === 0) layerId.current = nextLayerId++;

  useEffect(() => {
    if (!isLayer) return;
    const id = layerId.current;
    layerStack.push(id);
    return () => {
      // Remove by identity, not pop(): unmount order isn't guaranteed to mirror
      // mount order, and StrictMode mounts twice.
      for (let i = layerStack.length - 1; i >= 0; i--) {
        if (layerStack[i] === id) layerStack.splice(i, 1);
      }
    };
  }, [isLayer]);

  useEffect(() => {
    if (inert) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.defaultPrevented) return;
      if (!/^Key[A-Z]$/.test(e.code)) return;
      // Layer gate. An unclaimed stack means the base surface owns the chord;
      // otherwise only the top layer does, bound letter or not.
      const top = layerStack.length ? layerStack[layerStack.length - 1] : undefined;
      if (isLayer ? top !== layerId.current : top !== undefined) return;
      const action = latest.current[e.code.slice(3)];
      if (!action) return;
      const dialog = (e.target as HTMLElement | null)?.closest?.(
        '[role="dialog"],[aria-modal="true"]',
      );
      const scope = scopeRef?.current;
      if (dialog && scope && !dialog.contains(scope)) return;
      e.preventDefault();
      action();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [scopeRef, isLayer, inert]);
}

export default useAltHotkeys;
